// The donut charts a saved report can draw, and which ones it draws.
//
// A report owner picks them in the Summary section's "Choose metrics" panel;
// the choice is stored per report at `report.sections.charts.metrics`. A
// report that has never chosen draws the workspace default, which the server
// resolves from the Report Settings checkboxes (Report#chart_metrics) and
// hands to the page as `chartMetrics`. Report::CHART_METRICS mirrors the keys
// below — keep the two in the same order.
//
// Paid media donuts are not in the picker. They still appear on their own
// whenever a report carries boosted-post data, as they always have.

export const CHART_METRICS = [
  {
    key: "impressions",
    label: "Impressions",
    hint: "Times your content was shown",
  },
  {
    key: "views",
    label: "Views",
    hint: "Times your content was read or watched",
  },
  {
    // Reach is unique per post, not per campaign: the same person reached by
    // three posts counts three times once the posts are added up. The donut
    // is still useful, it just must not claim to be audience size.
    key: "impressions_unique",
    label: "Reach",
    hint: "Each post's reach, added up",
  },
  {
    key: "engagements",
    label: "Engagements",
    hint: "Times people interacted with your content",
  },
  {
    key: "outbound_clicks",
    label: "Link clicks",
    hint: "Clicks on links in your content",
  },
  {
    key: "revenue",
    label: "Revenue",
    hint: "Revenue attributed to this campaign",
  },
];

export const CHART_METRIC_KEYS = CHART_METRICS.map((m) => m.key);

export const PAID_CHART_METRICS = ["paid_impressions", "ad_spend", "paid_reach"];

// What reports drew before charts were chosen per report, minus whatever the
// workspace hides. Calendar and event reports and the deliverables list still
// draw exactly this.
export function defaultChartMetrics({
  hideImpressions = false,
  hideViews = false,
  hideEngagements = false,
} = {}) {
  return ["impressions", "views", "engagements", "revenue"].filter((key) => {
    if (key === "impressions" && hideImpressions) return false;
    if (key === "views" && hideViews) return false;
    if (key === "engagements" && hideEngagements) return false;
    return true;
  });
}

// Catalog order, known keys only, no repeats — so ticking Reach after Revenue
// still puts it beside Views, and a stale key from a saved blob draws nothing.
export function orderChartMetrics(keys = []) {
  return CHART_METRIC_KEYS.filter((key) => keys.includes(key));
}

// The report's own choice when it has made one (an empty list is a choice:
// no donuts), otherwise `fallback`.
export function chosenChartMetrics(sections, fallback = null) {
  const chosen = sections && sections.charts && sections.charts.metrics;
  return Array.isArray(chosen) ? orderChartMetrics(chosen) : fallback;
}

// Every donut ReportCharts will try to draw, in order. A type with no data in
// the report is skipped at render time, as before.
export function chartTypes(chartMetrics) {
  return [...orderChartMetrics(chartMetrics), ...PAID_CHART_METRICS];
}

// Whether a Summary total also has a donut on this report. Drives the picker's
// "Charted" note, which used to be stamped on every total regardless.
export function isCharted(key, chartMetrics) {
  return Array.isArray(chartMetrics) && chartMetrics.includes(key);
}
